import router from 'next/router';
import { useEffect, useRef, useState } from 'react';

import Icon from '../Icon/Icon';
import styles from './DropDown.module.scss';

export interface DropDownProps {
  items: { label: string; value: string }[];
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  id: string;
  name?: string;
  updateUrl?: boolean;
}

export default function DropDown({
  items,
  placeholder = 'Maak je keuze...',
  value,
  onChange,
  className,
  name,
  id,
  updateUrl = false,
}: DropDownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUrlUpdate = (item: string) => {
    if (!name) {
      return;
    }

    // Update url params but stay on the same page
    router.push({
      pathname: router.pathname,
      query: { ...router.query, [name]: item },
    });
  };

  const handleSelect = (item: string) => {
    onChange(item);
    setIsOpen(false);

    if (updateUrl) {
      handleUrlUpdate(item);
    }
  };

  return (
    <div className={`${styles.dropdown} ${className || ''}`} ref={dropdownRef}>
      <div
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        role="combobox"
        aria-controls={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <span className={styles.value} data-placeholder={placeholder}>
          {items.find(item => item.value === value)?.label || ''}
        </span>
        {value && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={e => {
              e.stopPropagation();
              onChange('');
            }}
            aria-label="Clear selection"
          >
            <Icon icon="XMARK" className={styles.clearIcon} />
          </button>
        )}
        <Icon
          icon="NAV_ARROW_DOWN"
          className={`${styles.icon} ${isOpen ? styles.iconOpen : ''}`}
        />
      </div>
      {isOpen && (
        <ul
          id={id}
          className={styles.menu}
          role="listbox"
          aria-label={placeholder}
        >
          {items.map(item => (
            <li
              key={item.value}
              role="option"
              aria-selected={item.value === value}
            >
              <button
                type="button"
                className={`${styles.option} ${
                  item.value === value ? styles.selected : ''
                }`}
                onClick={() => {
                  handleSelect(item.value);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
